/**
 * Lightweight client-side A/B testing hook.
 *
 * - First call on a browser picks a random variant from `variants` and
 *   persists it to localStorage so the same visitor always sees the same
 *   variant on return.
 * - Tracks a stable anonymous `session_id` (also localStorage) so the
 *   backend can dedup events without identifying the user.
 * - `track(event)` POSTs to /api/public/ab/event — fire-and-forget; the
 *   backend dedupes by (experiment, variant, event, session_id), so a
 *   refresh-spammer can't inflate the numbers.
 *
 * Used by the StatsTicker copy experiment on the landing page.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANT_KEY_PREFIX = "xea_ab_variant_";
const SESSION_KEY = "xea_ab_session";

const ensureSessionId = () => {
  if (typeof window === "undefined") return "ssr";
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    // crypto.randomUUID is widely supported; fall back to Math.random for
    // ancient browsers — collision risk is irrelevant at this scale.
    sid =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const pickVariant = (experiment, variants) => {
  if (typeof window === "undefined") return variants[0];
  const key = VARIANT_KEY_PREFIX + experiment;
  let chosen = localStorage.getItem(key);
  if (!chosen || !variants.includes(chosen)) {
    chosen = variants[Math.floor(Math.random() * variants.length)];
    localStorage.setItem(key, chosen);
  }
  return chosen;
};

// Shared cache for the AB config (which variants are currently active per
// experiment). Fetched once per page load — promoted variants are sticky
// for ~hours so refetching on every hook mount is wasteful.
let _abConfigPromise = null;
const fetchAbConfig = () => {
  if (_abConfigPromise) return _abConfigPromise;
  _abConfigPromise = axios.get(`${API}/public/ab/config`)
    .then((r) => r.data?.experiments || {})
    .catch(() => ({}));
  return _abConfigPromise;
};

export const useAbVariant = (experiment, variants) => {
  // Pick variant once on mount using lazy useState init — pickVariant
  // reads localStorage (a real side-effect) and returns the persisted
  // choice if any. Lazy init guarantees one call regardless of re-renders.
  const [variant, setVariant] = useState(() => pickVariant(experiment, variants));

  // Local dedup so a single mount → multiple useEffect → only one fetch
  const trackedRef = useRef(null);
  if (trackedRef.current === null) trackedRef.current = new Set();

  // ── Honor server-side "promoted winner" if any ─────────────────────
  // After an admin clicks "Ship as default" on /admin/live, the server
  // returns ONLY that variant in /public/ab/config. We override our
  // localStorage-pinned variant on next page load — old visitors get the
  // winner without a forced re-roll.
  useEffect(() => {
    let cancelled = false;
    fetchAbConfig().then((cfg) => {
      if (cancelled) return;
      const exp = cfg?.[experiment];
      const promoted = exp?.promoted;
      if (promoted && promoted !== variant) {
        try {
          localStorage.setItem(VARIANT_KEY_PREFIX + experiment, promoted);
        } catch (_) { /* ignore */ }
        setVariant(promoted);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiment]);

  const track = (event) => {
    const dedupKey = `${experiment}:${variant}:${event}`;
    if (trackedRef.current.has(dedupKey)) return;
    trackedRef.current.add(dedupKey);
    try {
      const sid = ensureSessionId();
      // Fire-and-forget — landing page must never wait on analytics
      axios.post(`${API}/public/ab/event`, {
        experiment,
        variant,
        event,
        session_id: sid,
      }).catch(() => { /* network errors swallowed */ });
    } catch (_) { /* localStorage disabled etc — silently ignore */ }
  };

  // Auto-fire a `view` event once on mount per variant.
  useEffect(() => {
    track("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return { variant, track };
};
